'use client';

import React from 'react';
import Image from 'next/image';

import { logTrackingEvent } from '@/utils/tracking';

import { DiscoveryFieldTitle } from '@/components/discovery-questions-input/field-title';
import { Field } from '@/components/ui/field';
import { Toggle } from '@/components/ui/toggle';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { StreamlineCheck } from '@tac/icons';

import { ALL_BODY_STYLE_LABEL, BODY_STYLE_KEYS, BODY_STYLE_OPTIONS } from './constants';
import type { BodyStyleTogglesProps } from './types';

const BODY_STYLE_IMAGE_WIDTH = 120;
const BODY_STYLE_IMAGE_HEIGHT = 50;

const BodyStyleToggles = ({ value, onChange }: BodyStyleTogglesProps) => {
  const isAllTypes = value.length === 0;

  const handleAllTypesPress = (pressed: boolean) => {
    if (pressed) {
      onChange([]);
    }
  };

  const handleGroupValueChange = (newValue: string[]) => {
    const next = newValue ?? [];
    if (next.length > 0) {
      const added = next.filter((k) => !value.includes(k));
      added.forEach((bodyStyleKey) =>
        logTrackingEvent({
          Category: 'Discovery',
          Action: 'BodyStyle:Selected',
          Label: bodyStyleKey,
        })
      );
    }
    onChange(next);
  };

  return (
    <Field className="w-full">
      <DiscoveryFieldTitle>Select your preferred body types</DiscoveryFieldTitle>
      <div className="grid w-full grid-cols-1 gap-y-3">
        <Toggle
          pressed={isAllTypes}
          onPressedChange={handleAllTypesPress}
          className="flex w-full justify-start items-center"
        >
          {isAllTypes && <StreamlineCheck aria-hidden className="size-4" />}
          <span>{ALL_BODY_STYLE_LABEL}</span>
        </Toggle>
        <ToggleGroup
          type="multiple"
          value={value}
          onValueChange={handleGroupValueChange}
          className="grid w-full grid-cols-2 gap-3 md:grid-cols-3 [&>button]:min-w-0"
        >
          {BODY_STYLE_KEYS.map((bodyStyleKey) => {
            const { imageUrl, label } = BODY_STYLE_OPTIONS[bodyStyleKey];

            return (
              <ToggleGroupItem
                key={bodyStyleKey}
                value={bodyStyleKey}
                className="flex h-full min-w-0 flex-col justify-center items-center"
              >
                <Image
                  src={imageUrl}
                  alt=""
                  width={BODY_STYLE_IMAGE_WIDTH}
                  height={BODY_STYLE_IMAGE_HEIGHT}
                  sizes="120px"
                  className="min-w-0 max-w-[120px] max-h-[50px] object-contain"
                  draggable={false}
                />
                <span>{label}</span>
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
      </div>
    </Field>
  );
};

export default BodyStyleToggles;
export type { BodyStyleTogglesProps } from './types';
